import { useRef, useState } from "react";
import { toast } from "sonner";
import { Film, Link2, Trash2, Upload, Video } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LESSONS } from "@/lib/learn";

function embedUrl(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    const h = u.hostname.replace("www.", "");
    if (h === "youtube.com" && u.searchParams.get("v")) return `https://www.youtube.com/embed/${u.searchParams.get("v")}`;
    if (h === "youtu.be") return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
    if (h.endsWith("vimeo.com")) { const id = u.pathname.split("/").filter(Boolean).pop(); return id ? `https://player.vimeo.com/video/${id}` : null; }
    if (h.endsWith("loom.com")) return url.replace("/share/", "/embed/");
  } catch { return null; }
  return null;
}

function Player({ clip }) {
  if (clip.kind === "upload" && clip.file_id) {
    return <video controls preload="metadata" className="w-full rounded-lg bg-black" data-testid="lesson-clip-video" src={fileUrl(`/api/files/${clip.file_id}`)} />;
  }
  const embed = embedUrl(clip.url);
  if (embed) return <div className="aspect-video w-full overflow-hidden rounded-lg bg-black"><iframe title="Lesson clip" src={embed} className="h-full w-full" allow="fullscreen" allowFullScreen data-testid="lesson-clip-embed" /></div>;
  return <a href={clip.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-accent underline" data-testid="lesson-clip-link"><Video className="h-4 w-4" />Watch the clip</a>;
}

export default function LessonClip({ lessonId, clip, isAdmin, onChange }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [also, setAlso] = useState([]);
  const fileRef = useRef(null);
  const toggleAlso = (id) => setAlso((a) => (a.includes(id) ? a.filter((x) => x !== id) : [...a, id]));

  const saveLink = async () => {
    if (!url.startsWith("http")) return toast.error("Paste a full video link (YouTube, Vimeo or Loom).");
    setBusy(true);
    try {
      if (also.length) await api.post("/learn/clips/apply", { kind: "link", url, lesson_ids: [lessonId, ...also] });
      else await api.put(`/learn/clips/${lessonId}`, { kind: "link", url });
      toast.success(also.length ? `Clip added to ${also.length + 1} lessons` : "Clip added"); setOpen(false); setUrl(""); setAlso([]); onChange();
    }
    catch { toast.error("Could not save the link."); }
    setBusy(false);
  };
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("kind", "clip");
      const { data } = await api.post("/files", fd);
      if (also.length) await api.post("/learn/clips/apply", { kind: "upload", file_id: data.file_id, lesson_ids: [lessonId, ...also] });
      else await api.put(`/learn/clips/${lessonId}`, { kind: "upload", file_id: data.file_id });
      toast.success(also.length ? `Clip added to ${also.length + 1} lessons` : "Clip uploaded"); setOpen(false); setAlso([]); onChange();
    } catch { toast.error("Upload failed — videos must be under 15 MB. Use a link for longer clips."); }
    setBusy(false);
  };
  const remove = async () => { try { await api.delete(`/learn/clips/${lessonId}`); onChange(); } catch { toast.error("Could not remove."); } };

  if (!clip && !isAdmin) return null;

  return (
    <div className="mt-4 border-t pt-4" data-testid={`lesson-clip-${lessonId}`}>
      {clip ? (
        <div>
          <div className="mb-2 flex items-center justify-between">
            <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground"><Film className="h-3.5 w-3.5" />Watch</p>
            {isAdmin && (
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setOpen(true)} data-testid="lesson-clip-replace">Replace</Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={remove} data-testid="lesson-clip-remove"><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            )}
          </div>
          <Player clip={clip} />
        </div>
      ) : isAdmin ? (
        <Button variant="outline" size="sm" className="rounded-full" onClick={() => setOpen(true)} data-testid="lesson-clip-add"><Film className="mr-1.5 h-3.5 w-3.5" />Add a clip</Button>
      ) : null}

      {isAdmin && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="sm:max-w-md" data-testid="lesson-clip-dialog">
            <DialogHeader><DialogTitle>Add a clip to this lesson</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <p className="mb-1.5 text-sm font-medium">Paste a video link</p>
                <div className="flex gap-2">
                  <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="YouTube, Vimeo or Loom URL" data-testid="lesson-clip-url" />
                  <Button onClick={saveLink} disabled={busy} data-testid="lesson-clip-save-link"><Link2 className="h-4 w-4" /></Button>
                </div>
              </div>
              <div className="relative text-center text-xs text-muted-foreground"><span className="bg-card px-2">or</span><div className="absolute inset-x-0 top-1/2 -z-10 border-t" /></div>
              <div>
                <Button variant="outline" className="w-full rounded-lg" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="lesson-clip-upload-btn"><Upload className="mr-1.5 h-4 w-4" />{busy ? "Uploading…" : "Upload a short video"}</Button>
                <input ref={fileRef} type="file" accept="video/*" className="hidden" onChange={upload} />
                <p className="mt-1.5 text-center text-xs text-muted-foreground">Up to 15 MB. For longer clips, paste a link instead.</p>
              </div>
              <div>
                <p className="mb-1.5 text-sm font-medium">Also add this clip to…</p>
                <div className="max-h-36 space-y-1 overflow-y-auto rounded-lg border p-2" data-testid="lesson-clip-also">
                  {LESSONS.filter((l) => l.id !== lessonId).map((l) => (
                    <label key={l.id} className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-secondary" data-testid={`lesson-clip-also-${l.id}`}>
                      <input type="checkbox" checked={also.includes(l.id)} onChange={() => toggleAlso(l.id)} className="h-3.5 w-3.5" />
                      <span className="truncate">{l.title}</span>
                    </label>
                  ))}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">Record once, reuse across related lessons.</p>
              </div>
            </div>
            <DialogFooter><Button variant="ghost" onClick={() => setOpen(false)}>Close</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
